<template>
    <button
        v-if="isSupported"
        type="button"
        @click="toggle"
        :disabled="disabled"
        :title="buttonTitle"
        :aria-label="buttonTitle"
        :class="[
            'rounded-2xl flex items-center justify-center transition shadow-sm flex-shrink-0 disabled:opacity-50 relative overflow-hidden gap-2',
            size === 'sm' ? 'h-[40px] w-[50px] px-3' : 'h-[46px] w-[50px] px-4',
            isListening
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
        ]"
    >
        <!-- Soundwave animation when talking -->
        <div
            v-if="isListening"
            class="flex items-end gap-[3px] h-3.5 flex-shrink-0"
        >
            <span
                class="w-[2.5px] bg-white rounded-full animate-soundwave-1"
                style="height: 100%"
            ></span>
            <span
                class="w-[2.5px] bg-white rounded-full animate-soundwave-2"
                style="height: 60%"
            ></span>
            <span
                class="w-[2.5px] bg-white rounded-full animate-soundwave-3"
                style="height: 85%"
            ></span>
            <span
                class="w-[2.5px] bg-white rounded-full animate-soundwave-4"
                style="height: 45%"
            ></span>
        </div>

        <ion-icon
            v-else
            name="mic-outline"
            :class="size === 'sm' ? 'text-lg' : 'text-xl'"
        ></ion-icon>
    </button>
</template>

<script setup>
import { computed, watch, onBeforeUnmount } from "vue";
import { useSpeechRecognition } from "@vueuse/core";

const props = defineProps({
    disabled: { type: Boolean, default: false },
    lang: { type: String, default: "en-US" },
    // 'md' matches the chat send button, 'sm' matches the new-topic send button
    size: { type: String, default: "md" },
    // Auto-stop this many ms after the coach stops talking (0 disables).
    autoStopMs: { type: Number, default: 1800 },
});

const emit = defineEmits(["transcript", "listen-start", "listen-end"]);

const { isSupported, isListening, result, start, stop, error } =
    useSpeechRecognition({
        lang: props.lang,
        continuous: true,
        interimResults: true,
    });

// Why we stopped, so the parent knows whether to auto-submit.
let stopReason = null;
let silenceTimer = null;

function clearSilenceTimer() {
    if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
    }
}

// Restart the "stopped talking" countdown; when it elapses we end the session.
function armSilenceTimer() {
    clearSilenceTimer();
    if (props.autoStopMs > 0) {
        silenceTimer = setTimeout(() => {
            if (isListening.value) {
                stopReason = "silence";
                stop();
            }
        }, props.autoStopMs);
    }
}

// Push the live (interim + final) transcript up to the parent as it grows,
// and reset the silence countdown on every new word.
watch(result, (val) => {
    emit("transcript", val);
    if (isListening.value && val) armSilenceTimer();
});

// A recognition error (e.g. mic permission denied) ends the session.
watch(error, (err) => {
    if (err && isListening.value) {
        stopReason = "error";
        stop();
    }
});

// Single source of truth for "stopped", covering silence, manual stop, errors
// and browser auto-timeout. Tell the parent why so it can decide to submit.
watch(isListening, (now, prev) => {
    if (prev && !now) {
        clearSilenceTimer();
        emit("listen-end", stopReason || "auto");
        stopReason = null;
    }
});

function toggle() {
    if (props.disabled) return;
    if (isListening.value) {
        stopReason = "manual";
        stop();
    } else {
        emit("listen-start"); // let the parent snapshot any already-typed text
        result.value = ""; // drop a stale transcript from a previous session
        start();
    }
}

const buttonTitle = computed(() => {
    if (error.value) {
        return "Microphone unavailable — check browser permissions";
    }
    return isListening.value ? "Stop dictation" : "Dictate your message";
});

onBeforeUnmount(() => {
    clearSilenceTimer();
    if (isListening.value) stop();
});
</script>

<style scoped>
@keyframes soundwave {
    0%,
    100% {
        transform: scaleY(0.3);
    }
    50% {
        transform: scaleY(1);
    }
}
.animate-soundwave-1 {
    animation: soundwave 0.8s ease-in-out infinite;
    transform-origin: bottom;
}
.animate-soundwave-2 {
    animation: soundwave 0.5s ease-in-out infinite;
    transform-origin: bottom;
}
.animate-soundwave-3 {
    animation: soundwave 0.7s ease-in-out infinite;
    transform-origin: bottom;
}
.animate-soundwave-4 {
    animation: soundwave 0.6s ease-in-out infinite;
    transform-origin: bottom;
}
</style>
